import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { apiJson, ApiError } from "../../lib/api";
import { getTenantUuid } from "@/app/ingress/tenant";

const LS_KEY = "ingress_recent_slugs_v1";
const LS_TENANT_KEY = "ingress_tenant_uuid_v1";

type PublishedApp = {
  slug: string;
  name: string;
  description?: string | null;
};

function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter(Boolean).slice(0, 10) : [];
  } catch {
    return [];
  }
}

function saveRecent(slug: string) {
  const s = String(slug || "").trim();
  if (!s) return;
  const next = [s, ...loadRecent().filter((x) => x !== s)].slice(0, 10);
  localStorage.setItem(LS_KEY, JSON.stringify(next));
}

function isUuidLike(s: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    s || ""
  );
}

function getTenantUuidFromLocation(search: string): string | null {
  try {
    const sp = new URLSearchParams(search || "");
    const tid = String(sp.get("tenant_id") || "").trim();
    if (tid && isUuidLike(tid)) return tid;
    return null;
  } catch {
    return null;
  }
}

export default function IngressLandingPage() {
  const nav = useNavigate();
  const loc = useLocation();

  const [slug, setSlug] = useState("");
  const [apps, setApps] = useState<PublishedApp[] | null>(null);
  const [recent, setRecent] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loadingApps, setLoadingApps] = useState(false);

  // Tenant selection:
  // 1) URL ?tenant_id=... (shareable)
  // 2) localStorage ingress_tenant_uuid_v1
  // 3) getTenantUuid() (your existing admin-selected tenant)
  const tenantUuid = useMemo(() => {
    const fromUrl = getTenantUuidFromLocation(loc.search);
    if (fromUrl) {
      localStorage.setItem(LS_TENANT_KEY, fromUrl);
      return fromUrl;
    }
    const fromLs = String(localStorage.getItem(LS_TENANT_KEY) || "").trim();
    if (fromLs && isUuidLike(fromLs)) return fromLs;

    const fromApp = String(getTenantUuid() || "").trim();
    if (fromApp && isUuidLike(fromApp)) {
      localStorage.setItem(LS_TENANT_KEY, fromApp);
      return fromApp;
    }
    return "";
  }, [loc.search]);

  useEffect(() => {
    setRecent(loadRecent());
  }, []);

  async function tryLoadPublishedApps() {
    setLoadingApps(true);
    setErr(null);
    try {
      if (!tenantUuid) {
        setApps([]);
        setErr("Missing tenant_id. Open this page with ?tenant_id=<tenant_uuid>.");
        return;
      }

      // ✅ Public endpoint (no admin role required)
      const data = await apiJson<any>(
        `/v1/ingress/apps?tenant_id=${encodeURIComponent(tenantUuid)}&published=true`
      );

      const list: PublishedApp[] = Array.isArray(data)
        ? data
            .filter((x: any) => x && typeof x === "object")
            .map((x: any) => ({
              slug: String(x.slug ?? ""),
              name: String(x.name ?? x.slug ?? ""),
              description: x.description ?? null,
            }))
            .filter((x: any) => x.slug)
        : [];

      setApps(list);
    } catch (e: any) {
      setApps(null);
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoadingApps(false);
    }
  }

  useEffect(() => {
    tryLoadPublishedApps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantUuid]);

  function goRun(s: string) {
    const clean = String(s || "").trim();
    if (!clean) return;
    saveRecent(clean);
    setRecent(loadRecent());

    // keep tenant_id in URL for runner pages
    nav(`/app/ingress/${encodeURIComponent(clean)}?tenant_id=${encodeURIComponent(tenantUuid)}`);
  }

  const canRun = useMemo(() => slug.trim().length > 0, [slug]);

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h1 className="text-2xl font-semibold">Run an Ingress App</h1>
      <div className="mt-2 text-sm text-gray-600">
        Tenant:{" "}
        <code className="font-mono">
          {tenantUuid ? `${tenantUuid.slice(0, 8)}…` : "missing tenant_id"}
        </code>
      </div>

      <div className="mt-2 text-sm text-gray-600">
        Enter an app slug (provided by your admin) to run the workflow, or pick a published app below.
      </div>

      <div className="mt-5 flex gap-2">
        <input
          className="border rounded px-3 py-2 w-full font-mono text-sm"
          placeholder="e.g. starter-meeting-assistant"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && canRun) goRun(slug);
          }}
        />
        <button
          className="px-4 py-2 rounded bg-black text-white text-sm disabled:opacity-60"
          disabled={!canRun || !tenantUuid}
          onClick={() => goRun(slug)}
        >
          Run
        </button>
      </div>

      {recent.length > 0 && (
        <div className="mt-6">
          <div className="text-sm font-medium">Recent</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {recent.map((s) => (
              <button
                key={s}
                className="px-3 py-2 rounded border text-sm hover:bg-gray-50 font-mono"
                onClick={() => goRun(s)}
                disabled={!tenantUuid}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium">Published apps</div>
          <button
            className="px-3 py-2 rounded border text-sm hover:bg-gray-50"
            onClick={tryLoadPublishedApps}
            disabled={loadingApps}
          >
            {loadingApps ? "Loading…" : "Refresh"}
          </button>
        </div>

        {apps === null ? (
          <div className="mt-2 text-sm text-gray-600">
            Could not load published apps.
            {err ? <div className="mt-2 text-xs text-red-600 whitespace-pre-wrap">{err}</div> : null}
          </div>
        ) : apps.length === 0 ? (
          <div className="mt-2 text-sm text-gray-600">
            No published apps found for this tenant.
            {err ? <div className="mt-2 text-xs text-amber-700 whitespace-pre-wrap">{err}</div> : null}
          </div>
        ) : (
          <div className="mt-3 border rounded bg-white overflow-hidden">
            {apps.map((a) => (
              <div key={a.slug} className="px-4 py-3 border-t first:border-t-0 flex items-start justify-between gap-3">
                <div>
                  <div className="font-medium">{a.name}</div>
                  <div className="text-xs text-gray-500 font-mono">{a.slug}</div>
                  {a.description ? <div className="text-sm text-gray-600 mt-1">{a.description}</div> : null}
                </div>
                <Link
                  className="px-3 py-2 rounded bg-black text-white text-sm whitespace-nowrap"
                  to={`/app/ingress/${encodeURIComponent(a.slug)}?tenant_id=${encodeURIComponent(tenantUuid)}`}
                  onClick={() => saveRecent(a.slug)}
                >
                  Open
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-8 text-xs text-gray-500">
        Admins can publish apps in{" "}
        <Link className="text-blue-700 hover:underline" to="/admin/ingress/apps">
          Admin → Ingress Apps
        </Link>
        .
      </div>
    </div>
  );
}